import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../ThemeProvider";
import api from "../../services/api";
import { dialogs } from "../../context/DialogContext";
import { useDraggableModalPanel } from "../../hooks/useDraggableModalPanel";
import { ROLES_CONTACTO_ASOCIADO } from "../../constants/rolesContactoAsociado";
import { useCaptacionRelacionContacto } from "../../hooks/useCaptacionRelacionContacto";
import ModalRelacionContactoExistente from "../ModalRelacionContactoExistente";
import CamposDomicilioContacto from "../contactos/CamposDomicilioContacto";
import {
  domicilioContactoVacio,
  hidratarDomicilioContacto,
  rellenarDomicilioSiFalta,
  payloadDomicilioContacto,
  fichaDomicilioDesdeForm,
} from "../../utils/domicilioContacto";
import { provinciaNombreDesdeValor } from "../../utils/provinciasEs";
import { useContactoSoftWarnings } from "../../hooks/useContactoSoftWarnings";

const CAMPOS = [
  { key: "nombre", label: "Nombre *", full: false },
  { key: "apellidos", label: "Apellidos", full: false },
  { key: "telefono", label: "Teléfono", full: false },
  { key: "email", label: "Email", full: false },
  { key: "rol", label: "Rol", full: true, isRol: true },
];

const FORM_VACIO = {
  nombre: "",
  apellidos: "",
  telefono: "",
  email: "",
  rol: "",
  ...domicilioContactoVacio(),
};

export default function ModalCrearContactoAsociado({
  open,
  onClose,
  onCreado,
  onActualizado,
  contactoEditar = null,
}) {
  const esEdicion = Boolean(contactoEditar);
  const { theme } = useTheme();
  const t = theme.colors;
  const isDark = theme.name === "dark";
  const relacionContacto = useCaptacionRelacionContacto({
    excludeContactoId: contactoEditar?.contacto_id || contactoEditar?.id || null,
    origen: "crm_contacto_asociado",
  });
  const [form, setForm] = useState({ ...FORM_VACIO });
  const [guardando, setGuardando] = useState(false);
  const { dragRef, startDrag, panelPositionStyle } = useDraggableModalPanel();
  const [puedeModificarRegistro, setPuedeModificarRegistro] = useState(true);
  const [placesContext, setPlacesContext] = useState(null);
  const softWarn = useContactoSoftWarnings(`contacto-asoc-${contactoEditar?.contacto_id || contactoEditar?.id || 'nuevo'}`);
  const ctxContacto = { idioma: 'es', pais: form.pais || 'ES' };
  const revisarSoft = (tipo, valor, key) => {
    softWarn.checkField(tipo, valor, ctxContacto).then((v) => {
      if (v !== valor) setForm((f) => ({ ...f, [key]: v }));
    });
  };

  useEffect(() => {
    if (!open) {
      relacionContacto.resetSeen();
      relacionContacto.closeQuiet();
      return;
    }
    setGuardando(false);
    if (!contactoEditar) {
      setForm({ ...FORM_VACIO });
      setPuedeModificarRegistro(true);
      return;
    }
    setForm({
      nombre: contactoEditar.nombre || "",
      apellidos: contactoEditar.apellidos || "",
      telefono: contactoEditar.telefono || "",
      email: contactoEditar.email || "",
      rol: contactoEditar.rol || "",
      ...hidratarDomicilioContacto(contactoEditar),
      provincia: provinciaNombreDesdeValor(
        contactoEditar.provincia || contactoEditar.domicilio_provincia || "",
      ),
    });
    setPuedeModificarRegistro(true);
    if (!contactoEditar.contacto_id) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/contactos/${contactoEditar.contacto_id}`);
        const c = data?.data;
        if (!alive || !c) return;
        setForm((f) => ({
          ...f,
          nombre: c.nombre || f.nombre,
          apellidos: c.apellidos || "",
          telefono: c.telefono || "",
          email: c.email || "",
          ...rellenarDomicilioSiFalta(f, c),
          provincia: provinciaNombreDesdeValor(
            rellenarDomicilioSiFalta(f, c).provincia || "",
          ),
        }));
        if (c.puedeModificar === false) setPuedeModificarRegistro(false);
      } catch {
        /* datos de la fila */
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contactoEditar]);

  useEffect(() => {
    if (!open) {
      setPlacesContext(null);
      return undefined;
    }
    let alive = true;
    const pob = (form.poblacion || "").trim();
    const prov = (form.provincia || "").trim();
    if (!pob) {
      setPlacesContext(null);
      return undefined;
    }
    (async () => {
      try {
        const { data } = await api.get("/geo/localidad-centro", {
          params: { poblacion: pob, provincia: prov, pais: "España" },
        });
        if (!alive) return;
        if (data?.ok && data.lat != null && data.lng != null) {
          setPlacesContext({ latitude: data.lat, longitude: data.lng });
        } else setPlacesContext(null);
      } catch {
        if (alive) setPlacesContext(null);
      }
    })();
    return () => { alive = false; };
  }, [open, form.poblacion, form.provincia]);

  if (!open) return null;

  const guardar = async () => {
    if (!form.nombre?.trim()) {
      await dialogs.alert("El nombre es obligatorio");
      return;
    }
    setGuardando(true);
    const payload = {
      nombre: form.nombre.trim(),
      apellidos: form.apellidos?.trim() || null,
      telefono: form.telefono?.trim() || null,
      email: form.email?.trim() || null,
      ...payloadDomicilioContacto(form),
    };
    const fichaDom = fichaDomicilioDesdeForm(form);
    const rol = form.rol?.trim() || "";
    try {
      if (esEdicion) {
        let nombre = payload.nombre;
        let apellidos = payload.apellidos || "";
        let telefono = payload.telefono || "";
        let email = payload.email || "";
        if (contactoEditar.contacto_id) {
          const { data } = await api.patch(`/contactos/${contactoEditar.contacto_id}`, {
            ...payload,
            tipo: "contacto",
          });
          const c = data?.data;
          if (c) {
            nombre = c.nombre;
            apellidos = c.apellidos || "";
            telefono = c.telefono || "";
            email = c.email || "";
          }
        }
        onActualizado?.({
          ...contactoEditar,
          nombre,
          apellidos,
          telefono,
          email,
          rol,
          ...fichaDom,
        });
        onClose();
        return;
      }
      const { data } = await api.post("/contactos", {
        ...payload,
        tipo: "contacto",
      });
      const c = data?.data;
      if (!c?.id) throw new Error("Sin datos de contacto");
      onCreado({
        id: crypto.randomUUID(),
        contacto_id: c.id,
        nombre: c.nombre,
        apellidos: c.apellidos || "",
        telefono: c.telefono || "",
        email: c.email || "",
        rol,
        ...fichaDom,
      });
      onClose();
    } catch (e) {
      const st = e?.response?.status;
      const msg =
        st === 502 || st === 503 || st === 504
          ? "El servidor no respondió a tiempo (error " +
            st +
            "). Comprueba si los datos se guardaron recargando la página antes de volver a intentarlo."
          : e?.response?.data?.error || e?.message ||
            (esEdicion ? "No se pudo actualizar el contacto" : "No se pudo crear el contacto");
      await dialogs.alert(msg);
    } finally {
      setGuardando(false);
    }
  };

  const inpSt = {
    width: "100%",
    padding: "8px 12px",
    borderRadius: 8,
    fontSize: 13,
    background: isDark ? "#111827" : "#f9fafb",
    border: `1.5px solid ${isDark ? "#374151" : "#e5e7eb"}`,
    color: isDark ? "#f9fafb" : "#111827",
    outline: "none",
    fontFamily: "inherit",
  };

  const labelSt = {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: t.textMuted,
    display: "block",
    marginBottom: 5,
  };

  const modal = (
    <div
      data-modal-form
      onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.85)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2000,
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      <div
        ref={dragRef}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 608,
          maxWidth: "94vw",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          background: t.bgCard,
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 24px 60px rgba(0,0,0,0.4)",
          border: `1px solid ${t.border}`,
          position: "relative",
          ...panelPositionStyle,
        }}
      >
        <div
          style={{
            height: 3,
            flexShrink: 0,
            background: "linear-gradient(90deg, #34d399, #059669, #047857, #059669, #34d399)",
          }}
        />
        <div
          onMouseDown={startDrag}
          style={{
            padding: "20px 24px 0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
            cursor: "grab",
            userSelect: "none",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 10,
                background: "linear-gradient(135deg, #059669, #047857)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 18,
                boxShadow: "0 4px 12px rgba(5,150,105,0.35)",
              }}
            >
              📇
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: t.textPrimary }}>
                {esEdicion ? "Editar contacto asociado" : "Nuevo contacto asociado"}
              </h2>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: t.textMuted }}>
                {esEdicion
                  ? "Actualiza los datos del contacto y su rol en la propiedad"
                  : "Se creará en contactos y se vinculará a esta propiedad"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: `1px solid ${t.border}`,
              background: isDark ? "#1f2937" : "#f3f4f6",
              cursor: "pointer",
              fontSize: 16,
              color: t.textMuted,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ✕
          </button>
        </div>
        <div style={{ height: 1, flexShrink: 0, background: `linear-gradient(90deg, transparent, ${t.border}, transparent)` }} />
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px 8px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {CAMPOS.map(({ key, label, full, isRol }) => (
              <div key={key} style={{ gridColumn: full ? "span 2" : "span 1" }}>
                <label style={labelSt}>{label}</label>
                {isRol ? (
                  <>
                    <input
                      list="roles-contacto-asociado"
                      value={form.rol}
                      onChange={(e) => setForm((f) => ({ ...f, rol: e.target.value }))}
                      disabled={!puedeModificarRegistro}
                      style={{ ...inpSt, opacity: puedeModificarRegistro ? 1 : 0.6, cursor: puedeModificarRegistro ? "text" : "not-allowed" }}
                      placeholder="Ej. Hija/o, Administrador…"
                    />
                    <datalist id="roles-contacto-asociado">
                      {ROLES_CONTACTO_ASOCIADO.map((r) => (
                        <option key={r} value={r} />
                      ))}
                    </datalist>
                  </>
                ) : (
                  <input
                    value={form[key]}
                    onChange={(e) => {
                      const valor = e.target.value;
                      setForm((f) => ({ ...f, [key]: valor }));
                      if (key === "telefono" || key === "email") {
                        relacionContacto.check(
                          key === "telefono" ? valor : form.telefono,
                          key === "email" ? valor : form.email,
                        );
                      }
                    }}
                    onBlur={() => {
                      const valor = form[key] || '';
                      if (key === 'apellidos') revisarSoft('apellidos', valor, 'apellidos');
                      else if (key === 'telefono') revisarSoft('telefono', valor, 'telefono');
                      else if (key === 'email') revisarSoft('email', valor, 'email');
                    }}
                    disabled={!puedeModificarRegistro}
                    style={{ ...inpSt, opacity: puedeModificarRegistro ? 1 : 0.6, cursor: puedeModificarRegistro ? "text" : "not-allowed" }}
                    type={key === "email" ? "email" : key === "telefono" ? "tel" : "text"}
                  />
                )}
              </div>
            ))}
            <CamposDomicilioContacto
              form={form}
              setForm={setForm}
              inpSt={inpSt}
              labelSt={labelSt}
              isDark={isDark}
              disabled={!puedeModificarRegistro}
              placesLocalityContext={placesContext}
            />
          </div>
        </div>
        <div
          style={{
            padding: "16px 24px 20px",
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
            flexShrink: 0,
            borderTop: `1px solid ${t.border}`,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            disabled={guardando}
            style={{
              padding: "11px 24px",
              borderRadius: 10,
              border: `2px solid ${t.border}`,
              background: isDark ? "#1f2937" : "#f3f4f6",
              color: t.textMuted,
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={guardar}
            disabled={guardando || (esEdicion && !puedeModificarRegistro)}
            title={esEdicion && !puedeModificarRegistro ? "No puedes modificar este contacto: no está ligado a ninguna propiedad o captación tuya" : undefined}
            style={{
              padding: "11px 30px",
              borderRadius: 10,
              border: "none",
              background: esEdicion && !puedeModificarRegistro ? (isDark ? "#374151" : "#d1d5db") : "linear-gradient(135deg, #059669, #047857)",
              color: "#fff",
              cursor: guardando ? "wait" : (esEdicion && !puedeModificarRegistro ? "not-allowed" : "pointer"),
              fontFamily: "inherit",
              fontSize: 13,
              fontWeight: 700,
              boxShadow: esEdicion && !puedeModificarRegistro ? "none" : "0 4px 14px rgba(5,150,105,0.35)",
              opacity: guardando ? 0.7 : 1,
            }}
          >
            {guardando ? (esEdicion ? "Guardando…" : "Creando…") : (esEdicion ? "Guardar cambios" : "Crear y añadir")}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(
    <>
      {modal}
      {softWarn.modal}
      <ModalRelacionContactoExistente
        open={relacionContacto.open}
        matches={relacionContacto.matches}
        canal={relacionContacto.canal}
        contexto="contacto"
        onClose={relacionContacto.dismiss}
        onDescartar={() => {
          relacionContacto.dismissDiscard();
          onClose?.();
        }}
      />
    </>,
    document.body,
  );
}
